import React from "react";
import {
  ArrowLeft,
  Mail,
  Shield,
  CheckCircle,
  AlertCircle,
  Chrome,
  Lock,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function GmailSetupGuide() {
  const benefits = [
    {
      icon: <Shield size={24} />,
      title: "Secure OAuth Authentication",
      description:
        "No need to share your password. Gmail OAuth is the most secure way to connect.",
    },
    {
      icon: <CheckCircle size={24} />,
      title: "Easy One-Click Setup",
      description:
        'Just click "Connect Gmail" and authorize in your browser. No complex configuration needed.',
    },
    {
      icon: <Mail size={24} />,
      title: "Full Email Access",
      description:
        "Send emails and read replies automatically. Works with your existing Gmail account.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F0F6FF] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/email-outreach">
            <button className="flex items-center gap-2 text-[#5E78F4] hover:text-[#4A63D2] mb-4">
              <ArrowLeft size={20} />
              Back to Dashboard
            </button>
          </Link>
          <h1 className="text-[32px] font-[700] text-[#2A2A2A] mb-2">
            Gmail Integration Guide
          </h1>
          <p className="text-[16px] text-[#666] leading-relaxed">
            Connect your Gmail account in just a few clicks using secure Google
            OAuth authentication. This is the easiest and most secure way to
            integrate your Gmail for email outreach.
          </p>
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            Why Choose Gmail Integration?
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] rounded-[12px] flex items-center justify-center text-white mx-auto mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-2">
                  {benefit.title}
                </h3>
                <p className="text-[14px] text-[#666] leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* What is OAuth Section */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] rounded-[10px] flex items-center justify-center">
              <Lock className="text-white" size={24} />
            </div>
            <h2 className="text-[24px] font-[600] text-[#2A2A2A]">
              What is Gmail OAuth?
            </h2>
          </div>

          <div className="space-y-4 text-[16px] text-[#666] leading-relaxed">
            <p>
              <strong>OAuth (Open Authorization)</strong> is Google's secure
              authentication system that lets you authorize our app to access
              your Gmail without sharing your password.
            </p>
            <p>
              When you click "Connect Gmail," you'll be redirected to Google's
              official login page where you can safely authorize our app. Google
              will then provide us with a secure token that allows us to send
              emails and read replies on your behalf.
            </p>
          </div>

          <div className="mt-6 p-4 bg-[#E8F5E8] rounded-[10px] border border-[#4CAF50]">
            <div className="flex items-start gap-3">
              <Shield className="text-[#4CAF50] mt-1" size={20} />
              <div>
                <h3 className="font-[600] text-[#2A2A2A] mb-2">
                  Security Benefits
                </h3>
                <ul className="text-[14px] text-[#666] space-y-1">
                  <li>• Your Gmail password is never shared with our app</li>
                  <li>
                    • You can revoke access anytime from your Google Account
                    settings
                  </li>
                  <li>• All communications are encrypted end-to-end</li>
                  <li>
                    • OAuth tokens automatically expire and refresh for security
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            Step-by-Step Setup
          </h2>

          {/* Step 1 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                1
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Click "Connect Email Provider"
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              On the main dashboard, click the "Connect Email Provider" button
              to open the connection options.
            </p>
            <div className="ml-11 p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
              <img
                src="/attached_assets/generated_images/Email_dashboard_connect_button_4c53442e.png"
                alt="Dashboard with Connect Email Provider button highlighted"
                className="w-full rounded-[8px] shadow-sm"
              />
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                2
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Select "Gmail"
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              In the provider selection drawer, choose "Gmail" to start the
              OAuth authentication process.
            </p>
            <div className="ml-11 p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
              <img
                src="/attached_assets/generated_images/Provider_selection_drawer_interface_a5216dd5.png"
                alt="Provider selection with Gmail option highlighted"
                className="w-full rounded-[8px] shadow-sm"
              />
            </div>
          </div>

          {/* Step 3 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                3
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Authorize with Google
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              You'll be redirected to Google's secure login page. Sign in with
              your Gmail account credentials.
            </p>

            <div className="ml-11 space-y-4">
              <div className="p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
                <img
                  src="/attached_assets/generated_images/Google_OAuth_login_page_ec979f46.png"
                  alt="Google OAuth login page"
                  className="w-full rounded-[8px] shadow-sm"
                />
              </div>

              <div className="p-4 bg-[#FFF9E6] border border-[#FFE066] rounded-[10px]">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-[#FF8C00] mt-1" size={20} />
                  <div>
                    <h4 className="font-[600] text-[#2A2A2A] mb-2">
                      Important
                    </h4>
                    <p className="text-[14px] text-[#666]">
                      Make sure you're on an official Google domain
                      (accounts.google.com) before entering your credentials.
                      This ensures you're authenticating directly with Google,
                      not a third party.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                4
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Review Permissions
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              Google will show you what permissions our app is requesting.
              Review and click "Allow" to continue.
            </p>

            <div className="ml-11 space-y-4">
              <div className="p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
                <img
                  src="/attached_assets/generated_images/Gmail_permissions_approval_screen_6bc14c6b.png"
                  alt="Google OAuth permissions screen"
                  className="w-full rounded-[8px] shadow-sm"
                />
              </div>

              <div className="bg-[#F8F9FA] rounded-[10px] p-4">
                <h4 className="font-[600] text-[#2A2A2A] mb-3">
                  Permissions Requested:
                </h4>
                <ul className="text-[14px] text-[#666] space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#4CAF50] mt-0.5" size={16} />
                    <span>
                      <strong>Send emails on your behalf:</strong> Required to
                      send outreach emails from your Gmail account
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#4CAF50] mt-0.5" size={16} />
                    <span>
                      <strong>Read emails:</strong> Required to track replies
                      and email engagement
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#4CAF50] mt-0.5" size={16} />
                    <span>
                      <strong>View your email address:</strong> Required to
                      identify your account
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                5
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Connection Successful!
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              After authorization, you'll be redirected back to the app and see
              a success message. Your Gmail account is now connected!
            </p>

            <div className="ml-11 space-y-4">
              <div className="p-4 bg-[#E8F5E8] border border-[#4CAF50] rounded-[10px]">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-[#4CAF50]" size={20} />
                  <div>
                    <h4 className="font-[600] text-[#2A2A2A]">
                      Gmail Connected Successfully!
                    </h4>
                    <p className="text-[14px] text-[#666]">
                      You can now send emails and track replies through your
                      Gmail account.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
                <img
                  src="/attached_assets/generated_images/Connected_account_dashboard_analytics_17a3ca08.png"
                  alt="Dashboard showing connected Gmail account with analytics"
                  className="w-full rounded-[8px] shadow-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Managing Your Connection */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            Managing Your Gmail Connection
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-4">
                Viewing Connection Status
              </h3>
              <ul className="text-[14px] text-[#666] space-y-2">
                <li>
                  • Your connected Gmail account appears on the main dashboard
                </li>
                <li>• You can see email analytics and activity in real-time</li>
                <li>
                  • The connection status is always visible in the provider list
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-4">
                Disconnecting Your Account
              </h3>
              <ul className="text-[14px] text-[#666] space-y-2">
                <li>
                  • Click the "Disconnect" button next to your Gmail account
                </li>
                <li>
                  • This immediately revokes our app's access to your Gmail
                </li>
                <li>
                  • You can also revoke access from your Google Account settings
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Security & Privacy */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] rounded-[10px] flex items-center justify-center">
              <Users className="text-white" size={24} />
            </div>
            <h2 className="text-[24px] font-[600] text-[#2A2A2A]">
              Security & Privacy
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-4">
                How We Protect Your Data
              </h3>
              <ul className="text-[14px] text-[#666] space-y-2">
                <li>• OAuth tokens are encrypted and stored securely</li>
                <li>
                  • We only access emails needed for outreach functionality
                </li>
                <li>• No email content is stored permanently on our servers</li>
                <li>• All API communications use HTTPS encryption</li>
                <li>• Tokens automatically refresh to maintain security</li>
              </ul>
            </div>

            <div>
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-4">
                Your Control & Rights
              </h3>
              <ul className="text-[14px] text-[#666] space-y-2">
                <li>• Revoke access anytime from Google Account settings</li>
                <li>
                  • View all connected apps in your Google Security dashboard
                </li>
                <li>• Monitor app activity and access history</li>
                <li>• Contact support to delete any stored data</li>
                <li>• Request data export or account deletion</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-[#F0F6FF] rounded-[10px] border border-[#E1EAFF]">
            <div className="flex items-start gap-3">
              <Shield className="text-[#5E78F4] mt-1" size={20} />
              <div>
                <h4 className="font-[600] text-[#2A2A2A] mb-2">
                  Google Security Standards
                </h4>
                <p className="text-[14px] text-[#666]">
                  Our Gmail integration follows Google's OAuth 2.0 security
                  standards and best practices. We undergo regular security
                  reviews to maintain compliance with Google's API policies.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            Troubleshooting
          </h2>

          <div className="space-y-6">
            <div className="border-l-4 border-[#FF6B6B] pl-4">
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-2">
                Authorization Failed
              </h3>
              <ul className="text-[14px] text-[#666] space-y-1">
                <li>
                  • Make sure you're using a supported browser (Chrome, Firefox,
                  Safari, Edge)
                </li>
                <li>• Clear your browser cookies and try again</li>
                <li>• Disable popup blockers for this site</li>
                <li>
                  • Try authorizing in an incognito/private browsing window
                </li>
                <li>• Check if your company/network blocks OAuth redirects</li>
              </ul>
            </div>

            <div className="border-l-4 border-[#FFB800] pl-4">
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-2">
                Emails Not Sending
              </h3>
              <ul className="text-[14px] text-[#666] space-y-1">
                <li>
                  • Verify your Gmail account has sufficient sending quota
                </li>
                <li>• Check if you've reached Gmail's daily sending limits</li>
                <li>
                  • Ensure your account isn't flagged for spam or suspicious
                  activity
                </li>
                <li>• Try disconnecting and reconnecting your Gmail account</li>
              </ul>
            </div>

            <div className="border-l-4 border-[#4ECDC4] pl-4">
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-2">
                Connection Keeps Disconnecting
              </h3>
              <ul className="text-[14px] text-[#666] space-y-1">
                <li>• This is normal - OAuth tokens refresh automatically</li>
                <li>
                  • If disconnections persist, try re-authorizing your account
                </li>
                <li>• Check your Google Account security settings</li>
                <li>• Ensure you haven't revoked access from Google's side</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Revoking Access */}
        <div className="bg-white rounded-[20px] p-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            How to Revoke Access (If Needed)
          </h2>

          <div className="space-y-4">
            <p className="text-[16px] text-[#666]">
              If you ever want to completely remove our app's access to your
              Gmail account:
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600] mt-1">
                  1
                </div>
                <div>
                  <p className="text-[16px] text-[#2A2A2A] mb-2">
                    Go to your Google Account
                  </p>
                  <p className="text-[14px] text-[#666]">
                    Visit{" "}
                    <code className="bg-[#F5F5F5] px-2 py-1 rounded">
                      myaccount.google.com
                    </code>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600] mt-1">
                  2
                </div>
                <div>
                  <p className="text-[16px] text-[#2A2A2A] mb-2">
                    Navigate to Security → Third-party apps
                  </p>
                  <p className="text-[14px] text-[#666]">
                    Click "Security" in the sidebar, then "Third-party apps with
                    account access"
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600] mt-1">
                  3
                </div>
                <div>
                  <p className="text-[16px] text-[#2A2A2A] mb-2">
                    Find and remove our app
                  </p>
                  <p className="text-[14px] text-[#666]">
                    Look for "Email Outreach App" in the list and click "Remove
                    Access"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link to="/email-outreach">
            <button className="bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white px-8 py-3 rounded-[10px] font-[600] hover:opacity-90 transition-all">
              Back to Dashboard
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
